<!--
  CompanyAddressFields -- the structured postal address inputs the Company
  page uses TWICE: the ship-from ("Shipping address", columns address_*) and
  the billing address (columns billing_address_*). One field set, two
  prefixes, so the two cannot drift apart in labels, limits or behavior.

  Props
    - distributor   the loaded distributor record (read only here).
    - fieldPrefix   column prefix: "address_" or "billing_address_".
    - fieldAttrs    { line1, line2, city, state, postal_code, country,
                    phone } -> attribute bag spread onto each input. The
                    caller supplies the data-testid for each as a LITERAL
                    (the ui-runner's testid lint only sees literals, so a
                    prefix built here would hide every id from it). The
                    ship-from keeps its original ids ("company-address-line1").
    - disabled      render every input disabled (read-only view).
    - required      street, city, state and ZIP are needed to launch a store
                    (marked `*`). A blank one does NOT block the auto-save
                    (partial saves, Jake 2026-10-23): only a bad format does.
                    Suite/unit and country are never marked.
    - withPhone     render the ship-from phone (address_phone). Billing has
                    no phone here: the billing contact phone is its own field.

  Emits
    - update:field  ({ key, value }) -- the parent assigns it. This
                    component never mutates the record it was given.

  The state input is a 2-letter code (uppercased as typed) and the country a
  select of 2-letter codes only: the tf-deconetwork ship_from contract rejects
  anything else for the whole order.
-->
<template>
  <div>
    <v-text-field
      :model-value="distributor[key('line1')]"
      :label="req('Street address')"
      maxlength="255"
      :disabled="disabled"
      v-bind="attrsFor('line1')"
      @update:model-value="set('line1', $event)"
    ></v-text-field>
    <v-text-field
      :model-value="distributor[key('line2')]"
      label="Suite, unit, building (optional)"
      maxlength="255"
      :disabled="disabled"
      v-bind="attrsFor('line2')"
      @update:model-value="set('line2', $event)"
    ></v-text-field>
    <div class="d-flex ga-4">
      <v-text-field
        :model-value="distributor[key('city')]"
        :label="req('City')"
          maxlength="120"
        :disabled="disabled"
        v-bind="attrsFor('city')"
        @update:model-value="set('city', $event)"
      ></v-text-field>
      <v-text-field
        :model-value="distributor[key('state')]"
        :label="req('State')"
          hint="Two-letter code, e.g. FL"
        maxlength="2"
        :disabled="disabled"
        v-bind="attrsFor('state')"
        @update:model-value="set('state', String($event || '').toUpperCase())"
      ></v-text-field>
      <v-text-field
        :model-value="distributor[key('postal_code')]"
        :label="req('ZIP code')"
        :rules="[zipRule]"
        maxlength="20"
        :disabled="disabled"
        v-bind="attrsFor('postal_code')"
        @update:model-value="set('postal_code', $event)"
      ></v-text-field>
    </div>
    <div class="d-flex ga-4">
      <v-select
        :model-value="distributor[key('country')]"
        :items="countryOptions"
        item-title="title"
        item-value="value"
        label="Country"
        :disabled="disabled"
        v-bind="attrsFor('country')"
        @update:model-value="set('country', $event)"
      ></v-select>
      <v-text-field
        v-if="withPhone"
        :model-value="distributor.address_phone"
        label="Phone (optional)"
        hint="Printed as the sender's phone on shipments."
        maxlength="50"
        :disabled="disabled"
        v-bind="attrsFor('phone')"
        @update:model-value="$emit('update:field', { key: 'address_phone', value: $event })"
      ></v-text-field>
    </div>
  </div>
</template>

<script>
export default {
  name: "CompanyAddressFields",
  props: {
    distributor: { type: Object, required: true },
    fieldPrefix: { type: String, required: true },
    fieldAttrs: { type: Object, required: true },
    withPhone: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    // Read-only view (a caller without edit rights): every input disabled.
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:field"],
  data() {
    return {
      // ship_from.country is a 2-letter code in the tf-deconetwork
      // contract; MVP is US-only.
      countryOptions: [{ title: "United States", value: "US" }],
    };
  },
  methods: {
    req(label) {
      return this.required ? `${label} *` : label;
    },
    attrsFor(part) {
      return this.fieldAttrs[part] || {};
    },
    key(part) {
      return `${this.fieldPrefix}${part}`;
    },
    set(part, value) {
      this.$emit("update:field", { key: this.key(part), value });
    },
    // A US ZIP is 5 digits, optionally +4 (the server refuses anything else,
    // Bug Intake #417/#418: the form saves as you type, so a bad ZIP must be
    // named here and never sent). Blank is allowed (the field is optional on
    // its own); other countries keep their own postal shapes.
    zipRule(value) {
      const country = this.distributor[this.key("country")] || "US";
      const zip = String(value || "").trim();
      if (!zip || country !== "US") return true;
      return (
        /^\d{5}(-\d{4})?$/.test(zip) ||
        "ZIP code must be 5 digits (or ZIP+4), e.g. 90210."
      );
    },
  },
};
</script>
